// DatosPerfilForm.js — Los datos de la persona (los mismos campos con los que se
// registró) en un solo formulario reutilizable.
//
// Vivía dentro de CertificadosPage; se sacó aquí para que la página de perfil
// del proyecto y la sección `perfil_form` del Landing Builder usen exactamente
// el mismo formulario, con los mismos campos y el mismo guardado, y solo cambie
// la presentación:
//   variante 'tarjeta' → como siempre: rótulo encima del campo, en la tarjeta
//                        del perfil clásico.
//   variante 'filas'   → rótulo a la izquierda y campo a la derecha, para las
//                        páginas que replican un diseño propio.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { profileService, especialidadService, paisService } from '../../services/api';
import { useProject } from '../../contexts/ProjectContext';
import VerContrasena from '../VerContrasena';

const COLORS = {
  primary: '#0f355b',
  accent: '#14b6cb',
  text: '#374151',
  textLight: '#6b7280',
  textMuted: '#9ca3af',
  border: '#e5e7eb',
  bg: '#f8fafc',
};

const SectionTitle = styled.h3`
  color: ${COLORS.primary};
  font-size: 1.15rem;
  margin: 0 0 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: ${p => p.$mb || '1rem'};
`;

const Label = styled.label`
  display: block;
  font-size: ${p => p.$size || '0.85rem'};
  font-weight: 600;
  color: ${p => p.$color || COLORS.text};
  margin-bottom: 0.3rem;
`;

const Input = styled.input`
  width: 100%;
  padding: ${p => p.$padding || '0.55rem 0.75rem'};
  border: 1px solid ${p => p.$borde || COLORS.border};
  border-radius: ${p => p.$radio || '8px'};
  font-size: ${p => p.$size || '0.9rem'};
  font-family: inherit;
  box-sizing: border-box;
  transition: border 0.2s;
  &:focus { outline: none; border-color: ${p => p.$foco || COLORS.accent}; }
  &:read-only { background: ${COLORS.bg}; color: ${COLORS.textLight}; }
`;

const Select = styled.select`
  width: 100%;
  padding: ${p => p.$padding || '0.55rem 0.75rem'};
  border: 1px solid ${p => p.$borde || COLORS.border};
  border-radius: ${p => p.$radio || '8px'};
  font-size: ${p => p.$size || '0.9rem'};
  font-family: inherit;
  box-sizing: border-box;
  background: white;
  &:focus { outline: none; border-color: ${p => p.$foco || COLORS.accent}; }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Btn = styled.button`
  background: ${p => p.$bg || `linear-gradient(135deg, ${COLORS.primary}, #1a4a7a)`};
  color: ${p => p.$color || 'white'};
  border: ${p => p.$borde || 'none'};
  padding: ${p => p.$padding || '0.5rem 1.2rem'};
  border-radius: ${p => p.$radio || '8px'};
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  font-size: ${p => p.$size || '0.85rem'};
  transition: all 0.2s;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Msg = styled.div`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 1rem;
  background: ${p => p.$error ? '#fef2f2' : '#f0fdf4'};
  color: ${p => p.$error ? '#991b1b' : '#065f46'};
  border: 1px solid ${p => p.$error ? '#fecaca' : '#bbf7d0'};
`;

/* ---- Variante en filas: rótulo a la izquierda, campo a la derecha ---- */
const Fila = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${p => p.$gap || '2rem'};
  padding: ${p => p.$padding || '0.55rem 0'};
  @media (max-width: 700px) { flex-direction: column; gap: 0.4rem; }
`;

const FilaRotulo = styled.div`
  flex: 0 0 ${p => p.$ancho || '270px'};
  padding-top: 10px;
  font-size: ${p => p.$size || '16px'};
  font-weight: ${p => p.$peso || 700};
  color: ${p => p.$color || '#1f2b45'};
  line-height: 1.3;
  @media (max-width: 700px) { flex: 1 1 auto; padding-top: 0; }
`;

const FilaCampo = styled.div`
  flex: 1 1 auto;
  max-width: ${p => p.$ancho || '530px'};
  width: 100%;
`;

const Ayuda = styled.p`
  margin: 0.5rem 0 0;
  font-size: ${p => p.$size || '14px'};
  color: ${p => p.$color || '#6b7280'};
  line-height: 1.5;
`;

const Separador = styled.hr`
  border: none;
  border-top: 1px solid ${p => p.$color || '#e5e7eb'};
  margin: ${p => p.$margen || '1.4rem 0'};
`;

const DatosPerfilForm = ({
  profile,
  setProfile,
  registroConfig,
  convenios = [],
  loading,
  updateUser,
  variante = 'tarjeta',
  // Qué campos se muestran y en qué orden. Vacío = todos los del registro, en
  // el orden del registro (que es como se ha comportado siempre).
  campos = [],
  mostrarTitulo = true,
  titulo = 'Mis Datos',
  permitirPassword = false,
  estilos = {},
  textos = {},
}) => {
  const { proyecto } = useProject();
  const [form, setForm] = useState({});
  const [customValues, setCustomValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [especialidades, setEspecialidades] = useState([]);
  const [paises, setPaises] = useState([]);
  // Cambio de contraseña (solo en las páginas que lo piden).
  const [abrirPass, setAbrirPass] = useState(false);
  const [pass, setPass] = useState({ actual: '', nueva: '', confirmar: '' });
  const [passMsg, setPassMsg] = useState(null);
  const [passSaving, setPassSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        nombre: profile.nombre || '',
        telefono: profile.telefono || '',
        codigo_pais: profile.codigo_pais || '+57',
        convenio_id: profile.convenio_id || '',
        especialidad: profile.especialidad || '',
        pais: profile.pais || '',
        dni: profile.dni || '',
      });
      const cp = profile.campos_personalizados;
      setCustomValues(cp && typeof cp === 'object' && !Array.isArray(cp) ? { ...cp } : {});
    }
  }, [profile]);

  useEffect(() => {
    especialidadService.list().then(r => setEspecialidades(r.data?.especialidades || r.data || [])).catch(() => {});
    paisService.list().then(r => setPaises(r.data?.paises || r.data || [])).catch(() => {});
  }, []);

  // Lista ordenada de campos del registro (array nuevo u objeto legado).
  const camposLista = Array.isArray(registroConfig?.campos)
    ? registroConfig.campos
    : Object.entries(registroConfig?.campos || {}).map(([k, v]) => ({ key: k, ancho: 100, ...v }));

  const tl = (v, fb = '') => {
    if (v == null) return fb;
    if (typeof v === 'string') return v || fb;
    if (typeof v === 'object') return v.es || Object.values(v)[0] || fb;
    return fb;
  };

  const setF = (k, val) => setForm(prev => ({ ...prev, [k]: val }));
  const setC = (k, val) => setCustomValues(prev => ({ ...prev, [k]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await profileService.updateProfile({ ...form, campos_personalizados: customValues });
      if (res.data?.user) {
        setProfile(prev => ({ ...prev, ...res.data.user }));
        if (updateUser) updateUser({ nombre: form.nombre });
      }
      toast.success(textos.guardado || 'Datos actualizados correctamente');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al guardar');
    }
    setSaving(false);
  };

  const guardarPassword = async () => {
    setPassMsg(null);
    if (pass.nueva.length < 6) {
      setPassMsg({ error: true, text: 'La nueva contraseña debe tener al menos 6 caracteres' });
      return;
    }
    if (pass.nueva !== pass.confirmar) {
      setPassMsg({ error: true, text: 'Las contraseñas no coinciden' });
      return;
    }
    setPassSaving(true);
    try {
      await profileService.changePassword(pass.actual, pass.nueva);
      setPassMsg({ error: false, text: 'Contraseña actualizada correctamente' });
      setPass({ actual: '', nueva: '', confirmar: '' });
    } catch (e) {
      setPassMsg({ error: true, text: e.response?.data?.message || 'Error al cambiar la contraseña' });
    }
    setPassSaving(false);
  };

  if (loading || !profile) {
    return <p style={{ color: COLORS.textLight }}>{textos.cargando || 'Cargando datos...'}</p>;
  }

  const especialidadesOpts = (registroConfig?.especialidades?.length ? registroConfig.especialidades : especialidades)
    .map(e => (typeof e === 'string' ? e : e.nombre));
  const paisesOpts = (registroConfig?.paises?.length ? registroConfig.paises : paises)
    .map(p => (typeof p === 'string' ? p : p.nombre));

  const enFilas = variante === 'filas';
  const propsCampo = {
    $padding: estilos.padding_campo,
    $borde: estilos.color_borde_campo,
    $radio: estilos.radio_campo,
    $size: estilos.tamano_campo,
    $foco: estilos.color_borde_foco,
  };

  // Cada campo se arma igual en las dos variantes; solo cambia el envoltorio.
  const envolver = (etiqueta, contenido, extra = null) => (enFilas ? (
    <Fila $gap={estilos.gap_fila} $padding={estilos.padding_fila}>
      <FilaRotulo $ancho={estilos.ancho_etiqueta} $size={estilos.tamano_etiqueta} $peso={estilos.peso_etiqueta} $color={estilos.color_etiqueta}>
        {etiqueta}
      </FilaRotulo>
      <FilaCampo $ancho={estilos.ancho_campo}>
        {contenido}
        {extra}
      </FilaCampo>
    </Fila>
  ) : (
    <FormGroup>
      <Label>{etiqueta}</Label>
      {contenido}
      {extra}
    </FormGroup>
  ));

  const renderCampo = (c) => {
    const et = (def) => `${tl(c.etiqueta, def)}${c.requerido && !enFilas ? ' *' : ''}`;
    switch (c.key) {
      case 'nombre':
        return envolver(et('Nombre'), <Input {...propsCampo} value={form.nombre || ''} onChange={e => setF('nombre', e.target.value)} />);
      case 'email':
        return envolver(et('Correo electrónico'), <Input {...propsCampo} value={profile.email || ''} readOnly />);
      case 'telefono':
        return envolver(et('Teléfono'), (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <Input {...propsCampo} style={{ width: '80px', flexShrink: 0 }} value={form.codigo_pais || ''} onChange={e => setF('codigo_pais', e.target.value)} placeholder="+57" />
            <Input {...propsCampo} value={form.telefono || ''} onChange={e => setF('telefono', e.target.value)} placeholder="Teléfono" />
          </div>
        ));
      case 'convenio': {
        if (!convenios.length) return null;
        const convVal = form.convenio_id != null && form.convenio_id !== '' ? String(form.convenio_id) : '';
        return envolver(et('Convenio / Institución'), (
          <Select {...propsCampo} value={convVal} onChange={e => setF('convenio_id', e.target.value)}>
            <option value="">— Sin convenio —</option>
            {convenios.map(cv => <option key={cv.id} value={String(cv.id)}>{cv.nombre}</option>)}
          </Select>
        ));
      }
      case 'especialidad': {
        const opts = [...especialidadesOpts];
        if (form.especialidad && !opts.includes(form.especialidad)) opts.unshift(form.especialidad);
        return envolver(et('Especialidad'), opts.length > 0 ? (
          <Select {...propsCampo} value={form.especialidad || ''} onChange={e => setF('especialidad', e.target.value)}>
            <option value="">— Seleccionar —</option>
            {opts.map((e, i) => <option key={i} value={e}>{e}</option>)}
          </Select>
        ) : (
          <Input {...propsCampo} value={form.especialidad || ''} onChange={e => setF('especialidad', e.target.value)} />
        ));
      }
      case 'pais': {
        const opts = [...paisesOpts];
        if (form.pais && !opts.includes(form.pais)) opts.unshift(form.pais);
        return envolver(et('País'), opts.length > 0 ? (
          <Select {...propsCampo} value={form.pais || ''} onChange={e => setF('pais', e.target.value)}>
            <option value="">— Seleccionar —</option>
            {opts.map((p, i) => <option key={i} value={p}>{p}</option>)}
          </Select>
        ) : (
          <Input {...propsCampo} value={form.pais || ''} onChange={e => setF('pais', e.target.value)} />
        ));
      }
      case 'dni':
        // El número de identificación no se edita una vez registrado.
        return envolver(
          et('Documento de identidad (DNI)'),
          profile.dni
            ? <Input {...propsCampo} value={profile.dni} readOnly />
            : <Input {...propsCampo} value={form.dni || ''} onChange={e => setF('dni', e.target.value)} />
        );
      case 'subrol': {
        // El rol profesional define a qué contenidos accede: se muestra, no se edita.
        const catalogo = Array.isArray(proyecto?.config_json?.subroles) ? proyecto.config_json.subroles : [];
        const actual = catalogo.find(s => String(s.key) === String(profile.subrol || ''));
        const texto = tl(actual?.label, '') || profile.subrol || '';
        if (!texto) return null;
        return envolver(et('Perfil'), <Input {...propsCampo} value={texto} readOnly />);
      }
      case 'password':
        return null; // la contraseña tiene su propio bloque
      default:
        // Campo personalizado u otro definido en el registro
        return envolver(et(c.key), (
          <Input
            {...propsCampo}
            type={['email', 'tel', 'number', 'date'].includes(c.tipo) ? c.tipo : 'text'}
            value={customValues[c.key] || ''}
            onChange={e => setC(c.key, e.target.value)}
          />
        ));
    }
  };

  const visibles = camposLista.filter(c => c.visible !== false && c.key !== 'password');
  const elegidos = campos.length
    ? campos.map(k => visibles.find(c => c.key === k)).filter(Boolean)
    : visibles;
  const bloques = elegidos
    .map(c => ({ key: c.key, ancho: c.ancho || 100, jsx: renderCampo(c) }))
    .filter(b => b.jsx);

  const tieneCampos = bloques.length > 0;

  const botonGuardar = (
    <Btn
      onClick={handleSave}
      disabled={saving}
      $bg={estilos.color_boton}
      $color={estilos.color_texto_boton}
      $radio={estilos.radio_boton}
      $padding={estilos.padding_boton}
      $size={estilos.tamano_boton}
    >
      {saving ? (textos.guardando || 'Guardando...') : (textos.boton_guardar || 'Guardar Cambios')}
    </Btn>
  );

  // Bloque de contraseña del diseño: explicación + botón que abre el cambio.
  const bloquePassword = permitirPassword && envolver(textos.etiqueta_password || 'Contraseña', (
    <>
      <Ayuda $size={estilos.tamano_ayuda} $color={estilos.color_ayuda}>
        {textos.ayuda_password
          || 'Por seguridad, no mostramos su contraseña actual. Si desea cambiarla, haga clic en "Cambiar contraseña" y defina una nueva.'}
      </Ayuda>
      <div style={{ marginTop: '0.7rem' }}>
        <Btn
          type="button"
          onClick={() => setAbrirPass(v => !v)}
          $bg={estilos.color_boton_password || 'transparent'}
          $color={estilos.color_texto_boton_password || COLORS.text}
          $borde={estilos.borde_boton_password || `1px solid ${COLORS.border}`}
          $radio={estilos.radio_boton}
          $padding={estilos.padding_boton}
          $size={estilos.tamano_boton}
        >
          {textos.boton_password || 'Cambiar contraseña'}
        </Btn>
      </div>
      {abrirPass && (
        <div style={{ marginTop: '1rem' }}>
          {passMsg && <Msg $error={passMsg.error}>{passMsg.text}</Msg>}
          <FormGroup $mb="0.7rem">
            <Label>Contraseña actual</Label>
            <VerContrasena flotante>
              {tipo => (
                <Input {...propsCampo} type={tipo} value={pass.actual} onChange={e => setPass({ ...pass, actual: e.target.value })} />
              )}
            </VerContrasena>
          </FormGroup>
          <FormRow>
            <FormGroup $mb="0.7rem">
              <Label>Nueva contraseña</Label>
              <VerContrasena flotante>
                {tipo => (
                  <Input {...propsCampo} type={tipo} value={pass.nueva} onChange={e => setPass({ ...pass, nueva: e.target.value })} />
                )}
              </VerContrasena>
            </FormGroup>
            <FormGroup $mb="0.7rem">
              <Label>Confirmar nueva</Label>
              <VerContrasena flotante>
                {tipo => (
                  <Input {...propsCampo} type={tipo} value={pass.confirmar} onChange={e => setPass({ ...pass, confirmar: e.target.value })} />
                )}
              </VerContrasena>
            </FormGroup>
          </FormRow>
          <Btn
            onClick={guardarPassword}
            disabled={passSaving || !pass.actual || !pass.nueva}
            $bg={estilos.color_boton}
            $color={estilos.color_texto_boton}
            $radio={estilos.radio_boton}
            $padding={estilos.padding_boton}
            $size={estilos.tamano_boton}
          >
            {passSaving ? 'Cambiando...' : 'Guardar contraseña'}
          </Btn>
        </div>
      )}
    </>
  ));

  if (enFilas) {
    return (
      <>
        {mostrarTitulo && <SectionTitle>{titulo}</SectionTitle>}
        {bloques.map(b => <React.Fragment key={b.key}>{b.jsx}</React.Fragment>)}
        {permitirPassword && (
          <>
            <Separador $color={estilos.color_separador} $margen={estilos.margen_separador} />
            {bloquePassword}
          </>
        )}
        <Fila $gap={estilos.gap_fila} $padding={estilos.padding_fila}>
          <FilaRotulo $ancho={estilos.ancho_etiqueta} />
          <FilaCampo $ancho={estilos.ancho_campo}>{botonGuardar}</FilaCampo>
        </Fila>
      </>
    );
  }

  return (
    <>
      {mostrarTitulo && <SectionTitle>{titulo}</SectionTitle>}
      {tieneCampos ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 0.75rem' }}>
          {bloques.map(b => (
            <div key={b.key} style={{ flex: b.ancho === 50 ? '1 1 calc(50% - 0.4rem)' : '1 1 100%', minWidth: 0 }}>
              {b.jsx}
            </div>
          ))}
        </div>
      ) : (
        <FormRow>
          <FormGroup><Label>Nombre</Label><Input value={form.nombre || ''} onChange={e => setF('nombre', e.target.value)} /></FormGroup>
          <FormGroup><Label>Email</Label><Input value={profile.email || ''} readOnly /></FormGroup>
        </FormRow>
      )}
      {permitirPassword && bloquePassword}
      <div style={{ marginTop: '1rem' }}>{botonGuardar}</div>
    </>
  );
};

export default DatosPerfilForm;
